import Uppy, { UppyFile } from '@uppy/core';
import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import slugify from 'slugify';

import UppyUploader from 'modules/common/components/UppyUploader/UppyUploader';
import { getHeaders } from 'services/help-center/zendeskBackendGateway';
import config from 'modules/common/config';

interface FilesUploaderProps {
    uppyRef: React.MutableRefObject<Uppy | null>;
    onFilesChange?: (files: UppyFile[]) => void;
    disabled?: boolean;
}

export default function FilesUploader({ uppyRef, onFilesChange = () => {}, disabled = false }: FilesUploaderProps) {
    const [files, setFiles] = useState<UppyFile[]>([]);

    useEffect(() => {
        if (uppyRef.current) {
            const uppy = uppyRef.current;

            const handleFileAdded = (file: UppyFile) => {
                setFiles((prev) => [...prev, file]);
            };

            const handleFileRemoved = (file: UppyFile) => {
                setFiles((prev) => prev.filter((f) => f.id !== file.id));
            };

            uppy.on('file-added', handleFileAdded);
            uppy.on('file-removed', handleFileRemoved);

            return () => {
                uppy.off('file-added', handleFileAdded);
                uppy.off('file-removed', handleFileRemoved);
            };
        }
        return () => {};
    }, [uppyRef]);

    useEffect(() => {
        onFilesChange(files);
    }, [onFilesChange, files]);

    return (
        <Stack sx={{ height: '100%' }}>
            <UppyUploader
                documentType=""
                ref={uppyRef}
                shouldPreloadData={false}
                hideUploadedDocuments
                doneButtonHandler={undefined}
                hideUploadButton
                disabled={disabled}
                height="100%"
                options={{
                    autoProceed: false,
                    restrictions: {
                        // Zendesk linked attachments have a limit of 50 MB per file.
                        // If a file exceeds these limits, it will be dropped and not attached to the zendesk ticket.
                        maxFileSize: 6 * 1024 * 1024, // 6MB for now, but update when limit increases in AWS
                        allowedFileTypes: [
                            'image/*',
                            'video/*',
                            'application/pdf',
                            '.pdf',
                            '.csv',
                            '.xls',
                            '.xlsx',
                            '.doc',
                            '.docx',
                        ],
                    },
                    onBeforeFileAdded: (currentFile) => {
                        const sanitizedName = slugify(currentFile.name);
                        const modifiedFile = {
                            ...currentFile,
                            meta: { ...currentFile.meta, name: sanitizedName },
                            name: sanitizedName,
                        };
                        return modifiedFile;
                    },
                }}
                xhrOptions={{
                    formData: false,
                    endpoint: '/uploads.json',
                    headers: (file: UppyFile) => ({
                        ...getHeaders(),
                        'Uppy-File-Name': file.name,
                    }),
                    baseUrl: `${config.djangoURL?.replace(/\/+$/, '')}/zendesk/proxy`,
                }}
            />
        </Stack>
    );
}
